import {
  QuaggaJSCodeReader,
  QuaggaJSReaderConfig,
  QuaggaJSResultObject,
} from "@ericblade/quagga2";
import { BarCodeScanningResult } from "expo-camera";
import { clamp } from "lodash";
import {
  BarcodeReaders,
  DEFAULT_ASPECT_RATIO,
  DEFAULT_MAX_WIDTH,
  HORIZONTAL_PADDING_FACTOR,
} from "./constants";
import { ReaderData, ScannableAreaParams, ScannerDimensions } from "./types";

/**
 * Converts a QuaggaJSResultObject to a partial BarCodeScanningResult
 */
export function quaggaCodeResultToBarCodeScanningResult(
  result: QuaggaJSResultObject
) {
  const codeResult = result?.codeResult;

  return {
    data: codeResult?.code,
    type: codeResult?.format,
  } as BarCodeScanningResult;
}

/**
 * Computes The horizontal and vertical padding as a percentage value of the screen size
 */
export function computeScannableArea({
  screenWidth,
  screenHeight,
  maxWidth = DEFAULT_MAX_WIDTH,
  aspectRatio = DEFAULT_ASPECT_RATIO,
}: ScannableAreaParams): ScannerDimensions {
  const scannerWidth = clamp(
    screenWidth - 2 * screenWidth * HORIZONTAL_PADDING_FACTOR,
    maxWidth
  );

  const scannerHeight = clamp(scannerWidth / aspectRatio, 0, screenHeight);

  return {
    scannerHeight,
    scannerWidth,
  };
}

export function toDecodeArea(
  scannerDimensions: ScannerDimensions,
  screenWidth: number,
  screenHeight: number
) {
  const { scannerWidth, scannerHeight } = scannerDimensions;

  const areaHorizontal = `${
    (((screenWidth - scannerWidth) / screenWidth) * 100) / 2
  }%`;

  const areaVertical = `${
    (((screenHeight - scannerHeight) / screenHeight) * 100) / 2
  }%`;

  return {
    top: areaVertical,
    bottom: areaVertical,
    right: areaHorizontal,
    left: areaHorizontal,
  };
}

/**
 * Decode area is measured from center of screen as a percentage, maximum 50%
 * @returns
 */
export function toOverlayPaddingPercentage(
  scannerDimensions: ScannerDimensions,
  screenWidth: number,
  screenHeight: number
) {
  const { scannerWidth, scannerHeight } = scannerDimensions;

  const areaHorizontal = `${
    (((screenWidth - scannerWidth) / screenWidth) * 100) / 2
  }%`;
  const areaVertical = `${
    (((screenHeight - scannerHeight) / screenHeight) * 100) / 2
  }%`;

  return {
    top: areaVertical,
    bottom: areaVertical,
    right: areaHorizontal,
    left: areaHorizontal,
  };
}

/**
 * Manipulate ReaderData such that the following are true:
 * 1. Supplements are inserted immediately before min( pos(parent), min(supplementPositions) )
 * 2. Supplements are collated into a single listing
 * 3. Supplements are sorted by order of appearance
 * @param readerData
 * @returns
 */
export const readerDataToQuaggaReaders = (readerData: ReaderData) => {
  const barcodeReaderInfoList = Object.values(readerData)
    .filter((reader) => reader.enabled)
    .sort((a, b) => a.position - b.position)
    .map((reader) => ({
      info: BarcodeReaders[reader.value],
      reader: reader,
    }));

  const supplementConfigList = barcodeReaderInfoList
    .filter((barcodeReader) => !!barcodeReader.info.supplementOf)
    .reduce<
      Record<
        keyof ReaderData,
        { config: QuaggaJSReaderConfig; minPosition: number }
      >
    >((result, current) => {
      const { supplementOf } = current.info;

      if (!supplementOf) {
        throw Error("Invalid state");
      }

      const coercedSupplementOf = supplementOf as QuaggaJSCodeReader;

      if (!result[coercedSupplementOf]) {
        result[coercedSupplementOf] = {
          config: {
            format: coercedSupplementOf,
            config: {
              supplements: [current.reader.value],
            },
          },
          minPosition: current.reader.position,
        };
      } else {
        result[coercedSupplementOf].config.config.supplements.push(
          current.reader.value
        );
        const existingMinPosition = result[coercedSupplementOf].minPosition;
        const currentPosition = current.reader.position;
        result[coercedSupplementOf].minPosition = Math.min(
          existingMinPosition,
          currentPosition
        );
      }

      return result;
    }, {} as any);

  const manipulatedReaders: (QuaggaJSCodeReader | QuaggaJSReaderConfig)[] =
    Object.values(readerData)
      .filter((r) => r.enabled && !BarcodeReaders[r.value].supplementOf)
      .sort((a, b) => a.position - b.position)
      .map((r) => r.value);

  for (const [mainFormat, supplementConfig] of Object.entries(
    supplementConfigList
  )) {
    const mainIndex = manipulatedReaders.findIndex((r) => r === mainFormat);
    insertAt(
      manipulatedReaders,
      mainIndex >= 0
        ? Math.min(mainIndex, supplementConfig.minPosition)
        : supplementConfig.minPosition,
      supplementConfig.config
    );
  }
  return manipulatedReaders;
};

function insertAt<T>(array: T[], index: number, element: T): T[] {
  const adjustedIndex = Math.min(index, array.length - 1);
  array.splice(adjustedIndex, 0, element);
  return array;
}
